import {makeSelectable, SelectableColumn} from "../../components/ColumnSelection";
import {
    createColumn,
    OptionBasicColumnKeys,
    ColumnParams,
    CategoryKey,
    CategoryTextKey,
    OptionReferenceColumnKey,
    StatusColumnKey,
    OptionTypeColumnKey,
} from "./BaseOptionsColumns";

/**
 * Settings version.
 *
 * Increase the version for every incompatible change to the rest of the
 * settings interface. Compatibility is measured by JSON.stringify/parse.
 *
 * Incompatible settings loaded from the back end are discarded and the
 * user is reverted back the the default settings.
 */
const VERSION = 3;

export interface IBaseOptionsViewUserSettings {
    /** The version of the base options settings instance. */
    version: number;

    /** Columns shown in the BaseOptionsView tables. */
    optionBasicColumns: {key: string, visible: boolean}[] | undefined;
}

/** initial UserSettings saved in global state */
export const initSettings = (): IBaseOptionsViewUserSettings => {
    return {
        version: VERSION,
        optionBasicColumns: undefined,
    };
};

/**
 * Validate a given instance of base options view settings.
 *
 * This method is called after the general user settings were deserialized
 * from persistent storage (database) and passed the version check.
 *
 * Here we can check the specific base option view settings version and
 * validate whether it's still compatible. It can also be used to migrate
 * an older base options view settings version to the current layout.
 *
 * This allows the base option view settings to change and become incompatible
 * without affecting the general user settings.
 */
export const validateSettings = (settings?: IBaseOptionsViewUserSettings): IBaseOptionsViewUserSettings => {
    if (settings && settings.version !== undefined && settings.version === VERSION) {
        // latest version; nothing to do
        return settings;
    }
    // incompatible settings, return the default base options settings.
    const defaultSettings = initSettings();
    if (settings) {
        // settings exist but their content is incompatible;
        // this is the place to migrate settings to the latest version
        settings.version = defaultSettings.version;
        settings.optionBasicColumns = defaultSettings.optionBasicColumns;
        return settings;
    } else {
        // no settings object for option basic view, yet
        return defaultSettings;
    }
};

/** Create columns for BaseOptionsView from the settings. */
export const loadColumns = (settings: IBaseOptionsViewUserSettings, params?: ColumnParams): SelectableColumn[] => {
    const useStatusEditable = (key: string): boolean => (
        key !== CategoryKey &&
        key !== OptionTypeColumnKey &&
        key !== CategoryTextKey &&
        key !== OptionReferenceColumnKey &&
        key !== StatusColumnKey
    );
    const processParams = (key: string, p?: ColumnParams): ColumnParams | undefined => {
        if (!useStatusEditable(key)) {
            // this column's editability is not linked to the rows's status
            return p;
        }
        if (!p) {
            // no provided column params. create our own params object.
            return {[key]: {keyEditable: "statusValueStateEditable"}};
        }
        if (!p.hasOwnProperty(key)) {
            // no provided params for this specific column. create our own params object.
            return Object.assign(p, {[key]: {keyEditable: "statusValueStateEditable"}});
        }
        // add the status parameter to the column's params
        Object.assign(p[key], {keyEditable: "statusValueStateEditable"});
        return p;
    };
    const newColumn = (key: string, p?: ColumnParams): SelectableColumn => (
        makeSelectable(createColumn(key, processParams(key, p)))
    );
    const loadColumn = (setting: {key: string, visible: boolean}, p?: ColumnParams): SelectableColumn => (
        makeSelectable(createColumn(setting.key, processParams(setting.key, p)), setting.visible)
    );

    const {optionBasicColumns} = settings;
    const optionBasic = optionBasicColumns && optionBasicColumns.length > 0
        ? optionBasicColumns.map(columnSetting => loadColumn(columnSetting, params))
        : OptionBasicColumnKeys.map(columnKey => newColumn(columnKey, params));

    return optionBasic;
};



// WEBPACK FOOTER //
// ./src/views/BaseOptionsView/BaseOptionsViewSettings.ts